import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserDetailsContext } from '../context/user-details/user-details'
import { ReportPeriodContext } from '../context/report-periods/report-periods'
import { UserNotificationsContext } from '../context/user-notifications/user-notifications'
import PartnerService from '../shared/services/partner/partner.service'
import UserDetailsService from '../shared/services/user-details/user-details.service'
import { IUserDetails } from '../shared/services/user-details/user-details.model'
import LocationService from '../shared/services/location/location.service'
import VehicleService from '../shared/services/vehicle/vehicle.service'
import SubsidiaryService from '../shared/services/subsidiary/subsidiary.service'
import ProjectService from '../shared/services/project/project.service'
import ContactSupplierService from '../shared/services/contact-supplier/contact-supplier.service'
import GroupService from '../shared/services/group/group.service'

const RECENT_CLIENTS_STORAGE_KEY_PREFIX = 'gca_recent_client_ids'
const RECENT_CLIENTS_LIMIT = 5

// Recents are scoped per partner organisation so client ids neither persist across
// accounts on a shared machine nor leak between partner books.
const recentClientsStorageKey = (partnerOrganisationId: string) =>
  `${RECENT_CLIENTS_STORAGE_KEY_PREFIX}:${partnerOrganisationId}`

// The partner organisation's id, whichever side of the switch the session is on:
// at partner level it IS the user's organisation; inside a client it is the client's
// partnerOrganisation.
export const resolvePartnerOrganisationId = (userDetails?: IUserDetails): string | undefined => {
  if (!userDetails) return undefined
  if (userDetails.isLoggedInAsClient) return userDetails.partnerOrganisation?.id
  return userDetails.organisation?.id
}

// Most recently opened client ids for one partner book, newest first. Used to group
// the banner's client switcher; purely a convenience, so storage failures fall back
// to an empty list.
export const getRecentClientIds = (partnerOrganisationId?: string): string[] => {
  if (!partnerOrganisationId) return []
  try {
    const raw = window.localStorage.getItem(recentClientsStorageKey(partnerOrganisationId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

const recordRecentClientId = (partnerOrganisationId: string | undefined, clientId: string) => {
  if (!partnerOrganisationId) return
  try {
    const next = [clientId, ...getRecentClientIds(partnerOrganisationId).filter(id => id !== clientId)].slice(0, RECENT_CLIENTS_LIMIT)
    window.localStorage.setItem(recentClientsStorageKey(partnerOrganisationId), JSON.stringify(next))
  } catch {
    // Recents are a convenience; unavailable storage must never block a switch.
  }
}

/**
 * The single owner of the partner "act as client" switch and exit flows.
 *
 * Switching organisations must tear down every client-scoped singleton cache, or the
 * previous client's data is served after the switch. Before this hook, three call sites
 * (the Clients page, the client detail page and the partner banner) each hand-cleared
 * different subsets of the caches, so at least one path leaked. All partner switch and
 * exit paths now come through here; the super-admin warning-banner teardown and the
 * logout teardown are intentionally separate flows and stay where they are. Keep
 * clearClientScopedCaches in step with the services that expose clearData().
 *
 * A switch from inside a client disconnects first, then connects. The disconnect is
 * what writes the "Partner stopped acting as this organisation" audit row for the
 * client being left (the connect endpoint never logs a stop), and it also means a
 * failed connect leaves the session at partner level, matching what the UI shows
 * after the failure re-sync. From partner level a single connect call suffices.
 */
export const useSwitchClient = () => {
  const navigate = useNavigate()
  const userDetailsContext = useContext(UserDetailsContext)
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const partnerService = new PartnerService()
  const userDetailsService = new UserDetailsService()

  const [switchingClientId, setSwitchingClientId] = useState<string | undefined>()
  const [isExiting, setIsExiting] = useState<boolean>(false)
  const isSwitching = isExiting || switchingClientId !== undefined

  const clearClientScopedCaches = () => {
    LocationService.getInstance().clearData()
    VehicleService.getInstance().clearData()
    SubsidiaryService.getInstance().clearData()
    ProjectService.getInstance().clearData()
    ContactSupplierService.getInstance().clearData()
    GroupService.getInstance().clearData()
    reportPeriodContext.clearData()
  }

  // The Actions panel is client-scoped UI, so leaving a client account closes it
  // (matching what Exit organisation has always done). Fire and forget: a failed
  // preference save must never block the switch.
  const closeActionsNav = () => {
    const userDetails = userDetailsContext.userDetails
    if (userDetails?.isLoggedInAsClient && userDetails.showActionsNav) {
      userDetailsService.editUserDetails({ ...userDetails, showActionsNav: false })
        .catch(err => console.log(err))
    }
  }

  const refreshSessionData = async () => {
    await userDetailsContext.forceUpdateUserDetails()
    await reportPeriodContext.forceUpdateReportPeriods()
  }

  // Connect to a client (from the partner console or directly from inside another
  // client account) and land on their dashboard. Resolves true only when the connect
  // AND the session refresh both landed; any failure re-syncs the UI to whatever the
  // session actually is, tells the user which step failed, and resolves false.
  const switchToClient = async (clientId: string): Promise<boolean> => {
    if (isSwitching) return false
    setSwitchingClientId(clientId)
    const partnerOrganisationId = resolvePartnerOrganisationId(userDetailsContext.userDetails)
    closeActionsNav()

    // A half-completed switch must never leave the screen showing one organisation
    // while the session points at another, so every failure path clears the caches
    // and refetches against the session as it now stands.
    const failSwitch = async (message: string): Promise<false> => {
      clearClientScopedCaches()
      try {
        await refreshSessionData()
      } catch (err) {
        console.log(err)
      }
      setSwitchingClientId(undefined)
      userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
      return false
    }

    // Leaving a client goes through disconnect first: that is the call that writes
    // the audit stop row for the client being left, and it means a connect failure
    // strands the session at partner level rather than half-switched.
    if (userDetailsContext.userDetails?.isLoggedInAsClient) {
      try {
        await partnerService.disconnectFromClient()
      } catch (err) {
        console.log(err)
        return failSwitch('There was an error leaving the current client account, please try again')
      }
    }
    try {
      await partnerService.connectToClient(clientId)
    } catch (err) {
      console.log(err)
      return failSwitch('There was an error opening the client account, please try again')
    }
    recordRecentClientId(partnerOrganisationId, clientId)
    clearClientScopedCaches()
    try {
      await refreshSessionData()
    } catch (err) {
      console.log(err)
      return failSwitch('There was an error loading the client account data, please try again')
    }
    setSwitchingClientId(undefined)
    navigate('/dashboard')
    return true
  }

  // Leave the client account and return to the partner console.
  const exitToPartner = async (): Promise<void> => {
    if (isSwitching) return
    setIsExiting(true)
    closeActionsNav()
    let disconnectFailed = false
    try {
      await partnerService.disconnectFromClient()
    } catch (err) {
      console.log(err)
      disconnectFailed = true
    }
    clearClientScopedCaches()
    try {
      await refreshSessionData()
    } catch (err) {
      console.log(err)
    }
    setIsExiting(false)
    if (disconnectFailed) {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error leaving the account, please try again', backgroundColor: 'text.primary' })
      navigate('/dashboard')
      return
    }
    navigate('/partner-clients')
  }

  return { switchToClient, exitToPartner, isSwitching, switchingClientId }
}
